import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  loading?: boolean;
}
export function DeleteAccountModal({
  isOpen,
  onClose,
  onConfirm,
  loading = false
}: DeleteAccountModalProps) {
  const [confirmText, setConfirmText] = useState('');
  const [understood, setUnderstood] = useState(false);
  if (!isOpen) return null;
  const isConfirmValid = confirmText === 'DELETE' && understood;
  const handleConfirm = async () => {
    if (!isConfirmValid) return;
    await onConfirm();
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-1821">
      <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto" data-id="element-1822">
        <div className="p-6" data-id="element-1823">
          <div className="flex items-start justify-between mb-4" data-id="element-1824">
            <div className="flex items-center" data-id="element-1825">
              <div className="bg-red-100 rounded-full p-2 mr-3" data-id="element-1826">
                <AlertTriangle size={24} className="text-red-600" data-id="element-1827" />
              </div>
              <h2 className="text-xl font-bold text-red-600" data-id="element-1828">Delete Account</h2>
            </div>
            <button onClick={onClose} disabled={loading} className="text-gray-400 hover:text-gray-600 disabled:opacity-50" data-id="element-1829">
              <X size={24} data-id="element-1830" />
            </button>
          </div>
          <div className="mb-6" data-id="element-1831">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4" data-id="element-1832">
              <p className="font-semibold text-red-900 mb-2" data-id="element-1833">
                ⚠️ This action cannot be undone
              </p>
              <p className="text-sm text-red-800" data-id="element-1834">
                Deleting your account will permanently remove all of your data,
                including:
              </p>
            </div>
            <ul className="space-y-2 mb-4" data-id="element-1835">
              <li className="flex items-start text-sm text-gray-700" data-id="element-1836">
                <span className="text-red-600 mr-2" data-id="element-1837">•</span>
                <span data-id="element-1838">All uploaded screenshots and conversation data</span>
              </li>
              <li className="flex items-start text-sm text-gray-700" data-id="element-1839">
                <span className="text-red-600 mr-2" data-id="element-1840">•</span>
                <span data-id="element-1841">All toxicity analysis results and reports</span>
              </li>
              <li className="flex items-start text-sm text-gray-700" data-id="element-1842">
                <span className="text-red-600 mr-2" data-id="element-1843">•</span>
                <span data-id="element-1844">Your account information and preferences</span>
              </li>
              <li className="flex items-start text-sm text-gray-700" data-id="element-1845">
                <span className="text-red-600 mr-2" data-id="element-1846">•</span>
                <span data-id="element-1847">Your subscription and billing information</span>
              </li>
              <li className="flex items-start text-sm text-gray-700" data-id="element-1848">
                <span className="text-red-600 mr-2" data-id="element-1849">•</span>
                <span data-id="element-1850">Access to all shared analysis links</span>
              </li>
            </ul>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4" data-id="element-1851">
              <p className="text-sm text-gray-700" data-id="element-1852">
                <strong data-id="element-1853">Note:</strong> If you have an active Pro subscription,
                it will be cancelled immediately. You will not be refunded for
                any remaining time in your billing period.
              </p>
            </div>
            <div className="mb-4" data-id="element-1854">
              <label className="flex items-start cursor-pointer" data-id="element-1855">
                <input type="checkbox" checked={understood} onChange={e => setUnderstood(e.target.checked)} disabled={loading} className="mt-1 mr-3" data-id="element-1856" />
                <span className="text-sm text-gray-700" data-id="element-1857">
                  I understand that this action is permanent and cannot be
                  undone. All my data will be permanently deleted.
                </span>
              </label>
            </div>
            <div data-id="element-1858">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1859">
                Type <strong data-id="element-1860">DELETE</strong> to confirm
              </label>
              <input type="text" value={confirmText} onChange={e => setConfirmText(e.target.value.toUpperCase())} disabled={loading} placeholder="DELETE" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1861" />
            </div>
          </div>
          <div className="flex gap-3" data-id="element-1862">
            <button onClick={onClose} disabled={loading} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1863">
              Cancel
            </button>
            <button onClick={handleConfirm} disabled={!isConfirmValid || loading} className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1864">
              {loading ? 'Deleting...' : 'Delete Account'}
            </button>
          </div>
          <p className="text-xs text-gray-500 text-center mt-4" data-id="element-1865">
            Need help? Contact us at{' '}
            <a href="mailto:toxicdetector25@gmail.com" className="text-purple-600 hover:underline" data-id="element-1866">
              toxicdetector25@gmail.com
            </a>
          </p>
        </div>
      </div>
    </div>;
}